import { readActorType } from "@/lib/analytics";
import type { ActorType } from "@/lib/analytics";
import { getCapabilityToken } from "@/lib/concierge/capability-token";

/**
 * The decision logic behind {@link useIsConciergeOperator}, pulled out as a
 * pure function so it is testable without touching `localStorage` or
 * rendering anything.
 *
 * Both conditions matter independently:
 * - `actor_type` alone is a display/analytics dimension only (see
 *   `lib/analytics/actor.ts`) with no bearing on authorization, so gating UI
 *   on it alone would show operator-only affordances to an operator session
 *   that never actually received a capability token (e.g. token delivery
 *   failed, or a future concierge link type that redeems without one).
 * - The capability token alone isn't a safe gate either: it is what
 *   authorizes the ad hoc add call server-side, but a UI decision keyed only
 *   on its presence would be one localStorage write away from surfacing an
 *   operator-only entry point in what is otherwise the salon's own session.
 *
 * Both must hold together.
 */
export function isConciergeOperatorSession(
  actorType: ActorType,
  capabilityToken: string | null
): boolean {
  return actorType === "concierge_operator" && capabilityToken !== null;
}

/**
 * Whether this session is a concierge operator's, with an ad hoc-add
 * capability token in hand - the gate for the KAN-193 paste-in entry point
 * and any future UI that should only ever be shown to Caroline shopping on a
 * salon's behalf, never to the salon herself.
 *
 * Reads `localStorage` directly (via `readActorType`/`getCapabilityToken`)
 * rather than subscribing to any store, so this is a plain synchronous read
 * on every render - cheap, and consistent with how both underlying values are
 * already read elsewhere in the app.
 *
 * @example
 * ```tsx
 * const isOperator = useIsConciergeOperator();
 * return isOperator ? <PasteInButton /> : null;
 * ```
 */
export function useIsConciergeOperator(): boolean {
  return isConciergeOperatorSession(readActorType(), getCapabilityToken());
}
